<template>
  <Card>
    <div class="flex items-start justify-between mb-6">
      <div>
        <h3 class="text-lg font-medium text-text">System Models</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Override which models handle each platform system role for this org.
          "Platform default" falls through to the operator-configured default.
        </p>
      </div>
      <Button v-if="canEdit" class="ml-4" :disabled="saving || !dirty" @click="save">
        {{ saving ? 'Saving…' : 'Save' }}
      </Button>
    </div>

    <div v-if="error" class="mb-4 text-sm text-red-600 dark:text-red-400">{{ error }}</div>
    <div v-if="successMsg" class="mb-4 text-sm text-green-600 dark:text-green-400">{{ successMsg }}</div>

    <div v-if="loading" class="space-y-4">
      <div v-for="i in 4" :key="i" class="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
    </div>

    <div v-else class="space-y-4">
      <div
        v-for="roleKey in ALL_MODEL_ROLE_KEYS"
        :key="roleKey"
        class="flex items-center gap-4"
      >
        <label class="w-44 text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">
          {{ MODEL_ROLE_LABELS[roleKey] }}
        </label>
        <select
          v-if="canEdit"
          v-model="draft[roleKey]"
          @change="dirty = true"
          class="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">Platform default</option>
          <option v-for="m in availableModels" :key="m.id" :value="m.id">
            {{ m.name || m.id }}
          </option>
        </select>
        <span v-else class="flex-1 text-sm text-gray-900 dark:text-gray-100">
          {{ draft[roleKey] || 'Platform default' }}
        </span>
      </div>
    </div>
  </Card>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { organizationsApi } from '../api';
import { useOrganization, useOrganizationPermissions } from '../composables';
import { Card, Button } from '@asabdev/asab-ui';
import {
  ALL_MODEL_ROLE_KEYS,
  MODEL_ROLE_LABELS,
  type ModelRoleKey,
} from '../types';

interface ModelOption {
  id: string;
  name: string;
}

const { organization } = useOrganization();
const { isAdmin } = useOrganizationPermissions();

const canEdit = isAdmin; // admins + owners can edit

const loading = ref(true);
const saving = ref(false);
const dirty = ref(false);
const error = ref('');
const successMsg = ref('');

const draft = ref<Record<ModelRoleKey, string>>({
  guardrail_judge: '',
  search_answer: '',
  search_summary: '',
  moderation: '',
});

const availableModels = ref<ModelOption[]>([]);

async function fetchModels() {
  try {
    const resp = await fetch('/v1/models');
    if (resp.ok) {
      const data = await resp.json();
      const items: ModelOption[] = (data.data || []).map((m: { id: string; name?: string }) => ({
        id: m.id,
        name: m.name || m.id,
      }));
      availableModels.value = items;
    }
  } catch {
    // Non-fatal: dropdowns still work but show only IDs already stored.
  }
}

async function fetchSettings() {
  if (!organization.value?.id) return;
  loading.value = true;
  error.value = '';
  try {
    const settings = await organizationsApi.getOrgSettings(organization.value.id);
    for (const key of ALL_MODEL_ROLE_KEYS) {
      draft.value[key] = settings.model_roles?.[key] ?? '';
    }
  } catch (e: unknown) {
    error.value = (e as Error)?.message || 'Failed to load settings';
  } finally {
    loading.value = false;
  }
}

async function save() {
  if (!organization.value?.id) return;
  saving.value = true;
  error.value = '';
  successMsg.value = '';
  try {
    const payload: Record<string, string> = {};
    for (const key of ALL_MODEL_ROLE_KEYS) {
      payload[key] = draft.value[key]; // "" = delete override
    }
    await organizationsApi.updateOrgSettings(organization.value.id, { model_roles: payload });
    dirty.value = false;
    successMsg.value = 'System models saved.';
    setTimeout(() => { successMsg.value = ''; }, 3000);
  } catch (e: unknown) {
    error.value = (e as Error)?.message || 'Failed to save settings';
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  await Promise.all([fetchSettings(), fetchModels()]);
});
</script>
